import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import TaskItem, { TYPES } from "@/components/TaskItem";
import { buildGpx, buildKml, downloadFile, slug } from "@/lib/ozExport";
import { ListChecks, Plus, Download } from "lucide-react";

export default function JobTasks({ jobId, jobTitle = "job", employees = [], tasks = [], center, onChange }) {
  const [list, setList] = useState(tasks);
  const [open, setOpen] = useState(null);
  const [newName, setNewName] = useState("");
  const [newType, setNewType] = useState("driving");
  const [savingId, setSavingId] = useState(null);
  const [adding, setAdding] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);

  const flightTasks = list.filter((t) => t.type === "flight");
  const exportTasks = (tasksToExport, fmt, name) => {
    const flights = tasksToExport.filter((t) => t.type === "flight");
    if (flights.length === 0) { toast.error("No flight tasks to export"); return; }
    if (fmt === "gpx") downloadFile(`${slug(name)}.gpx`, buildGpx(flights, name), "application/gpx+xml");
    else downloadFile(`${slug(name)}.kml`, buildKml(flights, name), "application/vnd.google-earth.kml+xml");
    toast.success(`Exported ${fmt.toUpperCase()} for OzRunways`);
  };

  const sync = (next) => { setList(next); onChange?.(next); };
  const patch = (id, p) => sync(list.map((t) => t.id === id ? { ...t, ...p } : t));

  const addTask = async () => {
    if (!newName.trim()) { toast.error("Task name required"); return; }
    setAdding(true);
    try {
      const r = await api.post(`/jobs/${jobId}/tasks`, { name: newName.trim(), type: newType, waypoints: [], zone: [], dangers: [] });
      sync([...list, r.data]); setNewName(""); setOpen(r.data.id);
      toast.success("Task added");
    } catch { toast.error("Failed to add task"); }
    finally { setAdding(false); }
  };

  const saveTask = async (t) => {
    setSavingId(t.id);
    try {
      const body = { name: t.name, type: t.type, assignee_id: t.assignee_id || null, assignee_ids: t.assignee_ids || [], notes: t.notes || "", done: !!t.done, waypoints: t.waypoints || [], zone: t.zone || [], dangers: t.dangers || [] };
      const r = await api.put(`/jobs/${jobId}/tasks/${t.id}`, body);
      patch(t.id, r.data);
      toast.success("Task saved");
    } catch { toast.error("Save failed"); }
    finally { setSavingId(null); }
  };

  const removeTask = async (id) => {
    if (!window.confirm("Delete this task?")) return;
    try { await api.delete(`/jobs/${jobId}/tasks/${id}`); sync(list.filter((t) => t.id !== id)); toast.success("Task deleted"); }
    catch { toast.error("Delete failed"); }
  };

  const copyTask = async (t, reverse) => {
    const body = {
      name: `${t.name} ${reverse ? "(return)" : "(copy)"}`,
      type: t.type,
      assignee_id: t.assignee_id || null,
      assignee_ids: [...(t.assignee_ids || [])],
      notes: t.notes || "",
      done: false,
      waypoints: reverse ? [...(t.waypoints || [])].reverse() : [...(t.waypoints || [])],
      zone: [...(t.zone || [])],
      dangers: [...(t.dangers || [])],
    };
    try {
      const r = await api.post(`/jobs/${jobId}/tasks`, body);
      sync([...list, r.data]); setOpen(r.data.id);
      toast.success(reverse ? "Return leg created" : "Task copied");
    } catch { toast.error("Copy failed"); }
  };

  const onDrop = async (toIdx) => {
    if (dragIdx === null || dragIdx === toIdx) { setDragIdx(null); setOverIdx(null); return; }
    const next = [...list];
    const [moved] = next.splice(dragIdx, 1);
    next.splice(toIdx, 0, moved);
    sync(next);
    setDragIdx(null); setOverIdx(null);
    try { await api.post(`/jobs/${jobId}/tasks/reorder`, { task_ids: next.map((t) => t.id) }); }
    catch { toast.error("Reorder failed"); }
  };

  const toggleDone = async (t) => {
    patch(t.id, { done: !t.done });
    try { await api.put(`/jobs/${jobId}/tasks/${t.id}`, { name: t.name, type: t.type, assignee_id: t.assignee_id || null, assignee_ids: t.assignee_ids || [], notes: t.notes || "", done: !t.done, waypoints: t.waypoints || [], zone: t.zone || [], dangers: t.dangers || [] }); }
    catch { toast.error("Update failed"); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="job-tasks">
      <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
        <h2 className="text-sm font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5"><ListChecks className="h-4 w-4" /> Tasks ({list.length})</h2>
        {flightTasks.length > 0 && (
          <div className="flex items-center gap-1.5" data-testid="job-oz-export">
            <span className="text-[11px] text-slate-400 mr-0.5">OzRunways ({flightTasks.length} flight{flightTasks.length > 1 ? "s" : ""}):</span>
            <button onClick={() => exportTasks(list, "gpx", jobTitle)} data-testid="export-job-gpx" className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium bg-blue-100 text-blue-700 hover:bg-blue-200"><Download className="h-3.5 w-3.5" /> GPX</button>
            <button onClick={() => exportTasks(list, "kml", jobTitle)} data-testid="export-job-kml" className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium bg-amber-100 text-amber-700 hover:bg-amber-200"><Download className="h-3.5 w-3.5" /> KML</button>
          </div>
        )}
      </div>

      {/* add task */}
      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Task name — e.g. Drive to site, Flight 1, Pick up product" data-testid="task-name-input" className="flex-1" onKeyDown={(e) => e.key === "Enter" && addTask()} />
        <Select value={newType} onValueChange={setNewType}>
          <SelectTrigger data-testid="task-type-select" className="sm:w-36"><SelectValue /></SelectTrigger>
          <SelectContent>{TYPES.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}</SelectContent>
        </Select>
        <Button onClick={addTask} disabled={adding} data-testid="add-task-btn" className="bg-slate-900 hover:bg-slate-800 text-white shrink-0"><Plus className="h-4 w-4 mr-1" /> Add</Button>
      </div>

      {list.length === 0 ? (
        <p className="text-sm text-slate-400">No tasks yet. Add driving, flight or pickup tasks and map their routes / zones.</p>
      ) : (
        <div className="space-y-2">
          {list.map((t, idx) => (
            <TaskItem
              key={t.id}
              t={t}
              idx={idx}
              isOpen={open === t.id}
              savingId={savingId}
              overIdx={overIdx}
              employees={employees}
              center={center}
              onToggleOpen={(id) => setOpen(open === id ? null : id)}
              onToggleDone={toggleDone}
              onCopy={copyTask}
              onRemove={removeTask}
              onPatch={patch}
              onSave={saveTask}
              onExport={(task, fmt) => exportTasks([task], fmt, `${jobTitle}-${task.name}`)}
              onDragStart={setDragIdx}
              onDragOver={(e, i) => { e.preventDefault(); setOverIdx(i); }}
              onDrop={onDrop}
              onDragEnd={() => { setDragIdx(null); setOverIdx(null); }}
            />
          ))}
        </div>
      )}
    </div>
  );
}
